import { useCallback, useEffect, useState } from "react";
import { getPublicWebsiteAsync } from "../api/OrganizationApi";
import {
  getTestimonialsByOrganizationAsync,
  getWebsiteMediaByOrganizationAsync,
  getWebsiteSettingsByOrganizationAsync,
  getWebsiteStatsByOrganizationAsync,
} from "../api/WebsiteApi";
import { Constants } from "../constants/constants";
import {
  EnumMediaType,
  enumMediaTypeToKey,
  PublicWebsiteResponse,
  Testimonial,
  WebsiteMedia,
  WebsiteSettings,
  WebsiteStat,
} from "../models/WebsiteModels";

interface UseWebsiteReturn {
  website: PublicWebsiteResponse | null;
  settings: WebsiteSettings | null;
  promoImages: WebsiteMedia[];
  beforeAfter: WebsiteMedia[];
  stats: WebsiteStat[];
  testimonials: Testimonial[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  updateSettings: (item: WebsiteSettings) => void;
  addPromo: (item: WebsiteMedia) => void;
  updatePromo: (item: WebsiteMedia) => void;
  removePromo: (id: string) => void;
  addBeforeAfter: (item: WebsiteMedia) => void;
  updateBeforeAfter: (item: WebsiteMedia) => void;
  removeBeforeAfter: (id: string) => void;
  addStat: (item: WebsiteStat) => void;
  updateStat: (item: WebsiteStat) => void;
  removeStat: (id: string) => void;
  addTestimonial: (item: Testimonial) => void;
  updateTestimonial: (item: Testimonial) => void;
  removeTestimonial: (id: string) => void;
}

const normalizeMediaType = (value: EnumMediaType): string =>
  enumMediaTypeToKey(value);

export const useWebsite = (
  organizationId: string | undefined,
  slug: string | undefined,
): UseWebsiteReturn => {
  const [website, setWebsite] = useState<PublicWebsiteResponse | null>(null);
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const [promoImages, setPromoImages] = useState<WebsiteMedia[]>([]);
  const [beforeAfter, setBeforeAfter] = useState<WebsiteMedia[]>([]);
  const [stats, setStats] = useState<WebsiteStat[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (silent?: boolean) => {
    if (!organizationId || !slug) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [websiteRes, mediaRes, statsRes, testimonialsRes, settingsRes] =
        await Promise.all([
          getPublicWebsiteAsync(slug),
          getWebsiteMediaByOrganizationAsync(organizationId),
          getWebsiteStatsByOrganizationAsync(organizationId),
          getTestimonialsByOrganizationAsync(organizationId),
          getWebsiteSettingsByOrganizationAsync(token, organizationId),
        ]);

      if (websiteRes.success && websiteRes.result) {
        setWebsite(websiteRes.result);
      } else {
        setError(websiteRes.message || "Failed to load website data");
      }

      if (settingsRes.success && settingsRes.result) {
        setSettings(settingsRes.result);
      }

      if (mediaRes.success) {
        const all = mediaRes.result ?? [];
        setPromoImages(all.filter((m) => normalizeMediaType(m.mediaType) === "promo"));
        setBeforeAfter(
          all.filter((m) => normalizeMediaType(m.mediaType) === "before_after"),
        );
      } else {
        setError(mediaRes.message || "Failed to load website media");
      }

      if (statsRes.success) {
        setStats(statsRes.result ?? []);
      } else {
        setError(statsRes.message || "Failed to load website stats");
      }

      if (testimonialsRes.success) {
        setTestimonials(testimonialsRes.result ?? []);
      } else {
        setError(testimonialsRes.message || "Failed to load testimonials");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to load website content");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [organizationId, slug]);

  useEffect(() => {
    load();
  }, [load]);

  const reload = useCallback(async () => {
    await load(true);
  }, [load]);

  const updateSettings = useCallback(
    (item: WebsiteSettings) => setSettings(item),
    [],
  );

  const addPromo = useCallback(
    (item: WebsiteMedia) => setPromoImages((prev) => [...prev, item]),
    [],
  );
  const updatePromo = useCallback(
    (item: WebsiteMedia) =>
      setPromoImages((prev) =>
        prev.map((media) => (media.id === item.id ? item : media)),
      ),
    [],
  );
  const removePromo = useCallback(
    (id: string) => setPromoImages((prev) => prev.filter((m) => m.id !== id)),
    [],
  );

  const addBeforeAfter = useCallback(
    (item: WebsiteMedia) => setBeforeAfter((prev) => [...prev, item]),
    [],
  );
  const updateBeforeAfter = useCallback(
    (item: WebsiteMedia) =>
      setBeforeAfter((prev) =>
        prev.map((media) => (media.id === item.id ? item : media)),
      ),
    [],
  );
  const removeBeforeAfter = useCallback(
    (id: string) =>
      setBeforeAfter((prev) => prev.filter((m) => m.id !== id)),
    [],
  );

  const addStat = useCallback(
    (item: WebsiteStat) => setStats((prev) => [...prev, item]),
    [],
  );
  const updateStat = useCallback(
    (item: WebsiteStat) =>
      setStats((prev) =>
        prev.map((stat) => (stat.id === item.id ? item : stat)),
      ),
    [],
  );
  const removeStat = useCallback(
    (id: string) => setStats((prev) => prev.filter((s) => s.id !== id)),
    [],
  );

  const addTestimonial = useCallback(
    (item: Testimonial) => setTestimonials((prev) => [...prev, item]),
    [],
  );
  const updateTestimonial = useCallback(
    (item: Testimonial) =>
      setTestimonials((prev) =>
        prev.map((t) => (t.id === item.id ? item : t)),
      ),
    [],
  );
  const removeTestimonial = useCallback(
    (id: string) =>
      setTestimonials((prev) => prev.filter((t) => t.id !== id)),
    [],
  );

  return {
    website,
    settings,
    promoImages,
    beforeAfter,
    stats,
    testimonials,
    loading,
    error,
    reload,
    updateSettings,
    addPromo,
    updatePromo,
    removePromo,
    addBeforeAfter,
    updateBeforeAfter,
    removeBeforeAfter,
    addStat,
    updateStat,
    removeStat,
    addTestimonial,
    updateTestimonial,
    removeTestimonial,
  };
};